import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { descriptorKeys } from './queries';
import { normalizeId } from '../utils/idNormalization';

/**
 * Hook to create a new descriptor
 * Handles invalidation of the descriptors query
 */
export function useCreateDescriptor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectID, name, choices }) => 
      QualAPI.createDescriptor(projectID, name, choices)
        .then(response => response.data),
    onSuccess: (data, variables) => {
      // Invalidate descriptors list for this project
      queryClient.invalidateQueries({ 
        queryKey: descriptorKeys.list(normalizeId(variables.projectID)) 
      });
    }
  });
}

/**
 * Hook to update a descriptor
 * Handles invalidation of the descriptors query
 */
export function useUpdateDescriptor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, name, choices, projectID }) => 
      QualAPI.updateDescriptor(id, name, choices)
        .then(response => response.data),
    onSuccess: (data, variables) => {
      // Invalidate descriptors list for this project
      if (variables.projectID) {
        queryClient.invalidateQueries({ 
          queryKey: descriptorKeys.list(normalizeId(variables.projectID)) 
        });
      }
    }
  });
}

/**
 * Hook to delete a descriptor
 * Handles invalidation of the descriptors query
 */
export function useDeleteDescriptor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, projectID }) => 
      QualAPI.deleteDescriptor(id, projectID)
        .then(response => response.data),
    onSuccess: (data, variables) => {
      // Invalidate descriptors list for this project
      queryClient.invalidateQueries({ 
        queryKey: descriptorKeys.list(normalizeId(variables.projectID)) 
      });
    }
  });
}

/**
 * Hook to create a multi choice for a descriptor
 * Handles invalidation of the descriptors query
 */
export function useCreateMultiChoice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ descriptorID, name, projectID }) => 
      QualAPI.createMultiChoice(descriptorID, name)
        .then(response => response.data),
    onSuccess: (data, variables) => {
      // Invalidate descriptors list for this project
      if (variables.projectID) {
        queryClient.invalidateQueries({ 
          queryKey: descriptorKeys.list(normalizeId(variables.projectID)) 
        });
      }
    }
  });
}

/**
 * Hook to create a selection for a transcript
 * Handles invalidation of the selections query
 */
export function useCreateSelection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ transcriptID, choiceID }) => 
      QualAPI.createMultiChoiceSelection(transcriptID, choiceID)
        .then(response => response.data),
    onSuccess: (data, variables) => {
      // Invalidate selections for this transcript
      queryClient.invalidateQueries({ 
        queryKey: descriptorKeys.selections(normalizeId(variables.transcriptID)) 
      });
    }
  });
}

/**
 * Hook for adding and selecting a choice in a single operation
 * First creates the choice, then creates the selection
 */
export function useAddAndSelectChoice() {
  const queryClient = useQueryClient();
  const createMultiChoice = useCreateMultiChoice();
  const createSelection = useCreateSelection();

  return useMutation({
    mutationFn: async ({ transcriptID, descriptorID, choiceText, projectID }) => {
      // First create the multi-choice
      const multiChoiceResult = await QualAPI.createMultiChoice(descriptorID, choiceText);
      const response = multiChoiceResult.data;

      // Response structure: { id: descriptorID, multi_choice: { id: choiceID, choice: text } }
      // We need the nested multi_choice.id for the selection
      const multiChoiceID = response.multi_choice.id;

      // Then create the selection with the correct multi_choice ID
      await QualAPI.createMultiChoiceSelection(transcriptID, multiChoiceID);

      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate descriptors list for this project (to show the new choice)
      if (variables.projectID) {
        queryClient.invalidateQueries({
          queryKey: descriptorKeys.list(normalizeId(variables.projectID))
        });
      }
      // Invalidate selections for this transcript (to show it's now selected)
      queryClient.invalidateQueries({
        queryKey: descriptorKeys.selections(normalizeId(variables.transcriptID))
      });
    }
  });
}
